import type { EChartsOption } from "./core";
import { formatterOf, type ValueFormat } from "./format";
import type { ChartTheme } from "./theme";

/**
 * ECharts 옵션 빌더 (순수 함수: 입력 + 테마 → 옵션). 화면은 이 함수를 직접 부르지 않고 components.tsx 의 부품을 쓴다.
 * 데이터 양에 따라 모양을 정한다: 점이 적으면 표식을 크게, 많으면 표식을 숨기고 확대 막대를 붙이며, 항목이 적은 막대는 값을 위에 적는다.
 * 빈 값은 잇지 않는다 (수집 안 된 구간을 0 이나 보간으로 꾸미지 않음).
 */

export type Series = {
  name: string;
  values: (number | null)[];
  /** CSS 색 또는 "var(--token)". tone.ok 처럼 의미 있는 색 */
  color?: string;
  /** 이전 기간 (같은 순서) → 점선 */
  previous?: (number | null)[];
  /** 값이 없을 때 툴팁·표 문구 (예: "상위 100 밖") */
  missing?: (string | null)[];
  /** 콤보 차트: 계열별 모양 */
  type?: "line" | "bar";
  /** 오른쪽 축에 그림 (단위가 다른 값) */
  axis?: "right";
  dashed?: boolean;
  /** 같은 색의 진하기 (순위 구간처럼 순서가 있는 계열) */
  opacity?: number;
  /** 막대마다 색 (상승·하락처럼 값마다 뜻이 다를 때) */
  pointColors?: (string | null | undefined)[];
};
export type Marker = { index: number; label: string };

export type SeriesSpec = {
  labels: string[];
  series: Series[];
  kind?: "line" | "area" | "bar";
  stacked?: boolean;
  /** 같은 날짜 안 비율(%) */
  normalized?: boolean;
  /** 가로 막대 (긴 항목 이름·순위 목록) */
  horizontal?: boolean;
  /** 순위: 1 이 위, 축을 뒤집음 */
  rank?: boolean;
  log?: boolean;
  max?: number;
  markers?: Marker[];
  format?: ValueFormat;
  rightFormat?: ValueFormat;
  /** 막대 위 값 표시 (생략하면 항목이 적을 때 자동) */
  showValues?: boolean;
};

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** 다크 배경에서는 옅은 진하기가 묻히므로 바닥을 올린다 (순서가 있는 진하기 계열·히트맵) */
const shade = (t: ChartTheme, a: number) => (t.dark ? 0.3 + a * 0.7 : a);

/** "rgb(r, g, b)" → 투명도 적용. 다른 형식이면 그대로 */
export function alpha(color: string, a: number): string {
  const m = /rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)/.exec(color);
  return m ? `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${a})` : color;
}

function axisStyle(t: ChartTheme) {
  return {
    axisLine: { lineStyle: { color: t.border } },
    axisTick: { show: false },
    axisLabel: { color: t.muted, fontSize: 11, fontFamily: t.font, hideOverlap: true },
    splitLine: { lineStyle: { color: t.border, opacity: 0.8 } },
    nameTextStyle: { color: t.muted, fontSize: 11 },
  };
}

export function tooltipBase(t: ChartTheme) {
  return {
    backgroundColor: t.surface,
    borderColor: t.border,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: t.text, fontSize: 12, fontFamily: t.font },
    extraCssText: `box-shadow: 0 8px 24px ${t.dark ? "rgba(0,0,0,.45)" : "rgba(15,23,42,.12)"}; border-radius: 8px;`,
    confine: true,
  };
}

function legendOf(t: ChartTheme, names: string[]) {
  if (names.length < 2) return { show: false };
  return {
    type: "scroll" as const,
    top: 0,
    left: 0,
    icon: "roundRect",
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 14,
    textStyle: { color: t.muted, fontSize: 11, fontFamily: t.font },
    pageTextStyle: { color: t.muted },
    pageIconColor: t.muted,
    pageIconInactiveColor: t.faint,
    inactiveColor: t.faint,
    data: names,
  };
}

const dot = (color: string) => `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:6px;background:${color}"></span>`;

/** 시간 순서 차트에서 모든 계열이 비어 있는 앞쪽 구간을 잘라 낸다 (수집 첫날 전 빈 칸이 화면을 차지하지 않게, 최소 7칸은 남김) */
function trimLeading(spec: SeriesSpec): SeriesSpec {
  if (spec.horizontal || spec.labels.length <= 7) return spec;
  const has = (i: number) => spec.series.some((s) => s.values[i] != null || s.previous?.[i] != null);
  let first = 0;
  while (first < spec.labels.length && !has(first)) first++;
  const start = Math.min(first, spec.labels.length - 7);
  if (start <= 0) return spec;
  const cut = <T,>(a: T[] | undefined) => a?.slice(start);
  return {
    ...spec,
    labels: spec.labels.slice(start),
    series: spec.series.map((s) => ({ ...s, values: s.values.slice(start), previous: cut(s.previous), missing: cut(s.missing), pointColors: cut(s.pointColors) })),
    markers: spec.markers?.filter((m) => m.index >= start).map((m) => ({ ...m, index: m.index - start })),
  };
}

/** 초 단위 축: 사람이 읽기 쉬운 간격(10초·30초·1분·5분 …)으로 */
const SECOND_STEPS = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];
function secondsAxis(max: number) {
  const step = SECOND_STEPS.find((x) => max / x <= 5) ?? 86400;
  return { interval: step, max: Math.max(step, Math.ceil(max / step) * step) };
}

/** 시계열·항목 비교 (선·영역·막대·누적·비율·가로·순위·이중 축) */
export function seriesOption(input: SeriesSpec, t: ChartTheme): EChartsOption {
  const spec = trimLeading(input);
  const { labels, series, kind = "line", stacked = false, normalized = false, horizontal = false, rank = false, log = false, markers = [] } = spec;
  const n = labels.length;
  const fmt = formatterOf(rank ? "rank" : normalized ? "percent" : spec.format);
  const rightFmt = formatterOf(spec.rightFormat ?? spec.format);
  const countLike = !normalized && !rank && (spec.format === undefined || spec.format === "count" || spec.format === "compact");
  const hasRight = series.some((s) => s.axis === "right");
  const totals = labels.map((_, i) => series.reduce((sum, s) => sum + (s.axis === "right" ? 0 : (s.values[i] ?? 0)), 0));
  const value = (s: Series, i: number): number | null => {
    const v = s.values[i];
    if (v == null) return null;
    if (normalized) return totals[i] ? (v / totals[i]!) * 100 : 0;
    if (log && v <= 0) return null;
    return v;
  };
  const dense = n > 40;
  const few = n <= 3;
  const zoom = !horizontal && n > 45;
  const anyBar = kind === "bar" || series.some((s) => s.type === "bar");
  const singleBar = anyBar && series.filter((s) => (s.type ?? (kind === "bar" ? "bar" : "line")) === "bar").length === 1;
  const showValues = spec.showValues ?? (singleBar && !stacked && n <= 12);

  // 툴팁이 원래 값·빈 값 사유를 보이도록 계열별 메타를 따로 둔다
  const meta: { name: string; color: string; raw: (number | null)[]; missing?: (string | null)[]; right: boolean }[] = [];
  const out: Record<string, unknown>[] = [];
  series.forEach((s, i) => {
    const color = t.color(s.color, i);
    const type = s.type ?? (kind === "bar" ? "bar" : "line");
    const right = s.axis === "right";
    const stack = stacked && !right ? "total" : undefined;
    meta.push({ name: s.name, color, raw: s.values, missing: s.missing, right });
    const item: Record<string, unknown> = {
      name: s.name,
      type,
      data: labels.map((_, j) => {
        const v = value(s, j);
        const pc = s.pointColors?.[j];
        return pc && v != null ? { value: v, itemStyle: { color: t.color(pc) } } : v;
      }),
      stack,
      [horizontal ? "xAxisIndex" : "yAxisIndex"]: right ? 1 : 0,
      itemStyle: { color, opacity: s.opacity == null ? undefined : shade(t, s.opacity), borderRadius: type === "bar" && !stack ? (horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]) : 0 },
      emphasis: { focus: "series" },
      connectNulls: false,
      animationDuration: 300,
    };
    if (type === "line") {
      Object.assign(item, {
        smooth: false,
        symbol: "circle",
        showSymbol: few || !dense,
        symbolSize: few ? 8 : 5,
        lineStyle: { width: 2, type: s.dashed ? "dashed" : "solid" },
        areaStyle: kind === "area" ? { opacity: stacked ? 0.35 : 0.14 } : undefined,
      });
    } else {
      Object.assign(item, { barMaxWidth: horizontal ? 22 : 36, barMinHeight: 0 });
      if (showValues)
        item.label = { show: true, position: horizontal ? "right" : "top", color: t.muted, fontSize: 11, fontFamily: t.font, formatter: (p: { dataIndex: number }) => (s.values[p.dataIndex] == null ? "" : fmt.value(s.values[p.dataIndex]!)) };
    }
    if (i === 0 && markers.length)
      item.markLine = {
        symbol: "none",
        silent: true,
        lineStyle: { color: t.faint, type: "dashed" },
        label: { color: t.muted, fontSize: 10, formatter: (p: { name?: string }) => p.name ?? "" },
        data: markers.filter((m) => labels[m.index] !== undefined).map((m) => ({ name: m.label, [horizontal ? "yAxis" : "xAxis"]: labels[m.index] })),
      };
    out.push(item);
    if (s.previous) {
      meta.push({ name: `${s.name} · 이전 기간`, color, raw: s.previous, right });
      out.push({
        name: `${s.name} · 이전 기간`,
        type: "line",
        data: labels.map((_, j) => s.previous![j] ?? null),
        [horizontal ? "xAxisIndex" : "yAxisIndex"]: right ? 1 : 0,
        itemStyle: { color, opacity: 0.55 },
        lineStyle: { width: 1.5, type: "dashed", opacity: 0.7 },
        symbol: "circle",
        showSymbol: few,
        symbolSize: 5,
        connectNulls: false,
      });
    }
  });

  // 왼쪽(기본) 축의 실제 최댓값 → 작은 건수는 위에 한 칸 여유, 초는 읽기 쉬운 간격
  const leftMax = Math.max(0, ...(stacked && !normalized ? totals : series.filter((x) => x.axis !== "right").flatMap((x) => [...x.values, ...(x.previous ?? [])]).filter((v): v is number => v != null)));
  const smallCount = countLike && !log && spec.max === undefined && leftMax < 5;
  const secAxis = spec.format === "seconds" && !normalized && leftMax > 0 ? secondsAxis(leftMax) : null;
  const category = {
    type: "category" as const,
    data: labels,
    boundaryGap: anyBar,
    inverse: horizontal,
    ...axisStyle(t),
    splitLine: { show: false },
  };
  const valueAxis = {
    type: (log ? "log" : "value") as "log" | "value",
    inverse: rank,
    min: rank || log ? 1 : undefined,
    max: normalized ? 100 : (spec.max ?? (smallCount ? Math.max(1, Math.ceil(leftMax)) + 1 : secAxis?.max)),
    interval: secAxis?.interval,
    minInterval: rank || countLike ? 1 : undefined,
    ...axisStyle(t),
    axisLabel: { ...axisStyle(t).axisLabel, formatter: (v: number) => fmt.axis(v) },
  };
  const rightAxis = {
    type: "value" as const,
    minInterval: 1,
    ...axisStyle(t),
    splitLine: { show: false },
    axisLabel: { ...axisStyle(t).axisLabel, formatter: (v: number) => rightFmt.axis(v) },
  };

  const legendNames = meta.map((m) => m.name);
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: t.font, color: t.text },
    animationDuration: 300,
    legend: legendOf(t, legendNames),
    grid: { left: 4, right: hasRight ? 8 : 18, top: legendNames.length > 1 ? 34 : 14, bottom: zoom ? 46 : 4 },
    tooltip: {
      ...tooltipBase(t),
      trigger: "axis",
      axisPointer: { type: anyBar ? "shadow" : "line", lineStyle: { color: t.faint }, shadowStyle: { color: alpha(t.faint, 0.12) } },
      formatter: (raw: unknown) => {
        const ps = (Array.isArray(raw) ? raw : [raw]) as { seriesIndex: number; dataIndex: number; axisValueLabel?: string; name?: string }[];
        if (!ps.length) return "";
        const i = ps[0]!.dataIndex;
        const head = `<div style="margin-bottom:4px;color:${t.muted}">${esc(labels[i] ?? ps[0]!.axisValueLabel ?? "")}</div>`;
        const rows = ps.map((p) => {
          const m = meta[p.seriesIndex];
          if (!m) return "";
          const v = m.raw[i] ?? null;
          const shown = v == null ? (m.missing?.[i] ?? fmt.value(null)) : m.right ? rightFmt.value(v) : normalized ? `${formatterOf("percent").value(value({ name: m.name, values: m.raw }, i))} · ${formatterOf(spec.format).value(v)}` : fmt.value(v);
          return `<div style="display:flex;justify-content:space-between;gap:16px"><span>${dot(m.color)}${esc(m.name)}</span><b style="font-weight:600">${esc(shown)}</b></div>`;
        });
        const total = stacked && !normalized && ps.length > 1 ? `<div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px;padding-top:4px;border-top:1px solid ${t.border}"><span>합계</span><b>${esc(fmt.value(totals[i] ?? 0))}</b></div>` : "";
        return head + rows.join("") + total;
      },
    },
    xAxis: horizontal ? [valueAxis, ...(hasRight ? [rightAxis] : [])] : category,
    yAxis: horizontal ? category : [valueAxis, ...(hasRight ? [rightAxis] : [])],
    dataZoom: zoom
      ? [
          { type: "inside", start: Math.max(0, 100 - (45 / n) * 100), end: 100 },
          { type: "slider", height: 18, bottom: 6, start: Math.max(0, 100 - (45 / n) * 100), end: 100, borderColor: t.border, fillerColor: alpha(t.accent, 0.15), textStyle: { color: t.muted, fontSize: 10 }, handleStyle: { color: t.surface, borderColor: t.faint }, dataBackground: { lineStyle: { color: t.faint }, areaStyle: { color: alpha(t.faint, 0.2) } } },
        ]
      : undefined,
    series: out as EChartsOption["series"],
  };
}

export type HeatSpec = {
  columns: string[];
  rows: { name: string; values: (number | null)[] }[];
  /** 순위 (낮을수록 진함, 100 밖) */
  rank?: boolean;
  format?: ValueFormat;
};

/** 순위 구간 (히트맵 범례·색) */
const RANK_BANDS = [
  { min: 1, max: 3, label: "1–3위", a: 0.92 },
  { min: 4, max: 10, label: "4–10위", a: 0.62 },
  { min: 11, max: 30, label: "11–30위", a: 0.38 },
  { min: 31, max: 100, label: "31–100위", a: 0.2 },
];

export function heatmapOption(spec: HeatSpec, t: ChartTheme): EChartsOption {
  const { columns, rows, rank = true } = spec;
  const fmt = formatterOf(rank ? "rank" : spec.format);
  const data: [number, number, number][] = [];
  rows.forEach((r, y) => r.values.forEach((v, x) => v != null && data.push([x, y, v])));
  const values = data.map((d) => d[2]);
  const cells = columns.length * rows.length;
  const short = (c: string) => (c.length === 10 && c[4] === "-" ? c.slice(5).replace("-", ".") : c);
  const visualMap = rank
    ? {
        type: "piecewise" as const,
        pieces: [...RANK_BANDS.map((b) => ({ min: b.min, max: b.max, label: b.label, color: alpha(t.accent, shade(t, b.a)) })), { min: 101, label: "100위 밖", color: t.surfaceMuted }],
        orient: "horizontal" as const,
        left: 0,
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { color: t.muted, fontSize: 11 },
      }
    : {
        type: "continuous" as const,
        min: values.length ? Math.min(...values) : 0,
        max: values.length ? Math.max(...values) : 1,
        orient: "horizontal" as const,
        left: 0,
        bottom: 0,
        itemHeight: 120,
        inRange: { color: [alpha(t.accent, 0.08), alpha(t.accent, 0.95)] },
        textStyle: { color: t.muted, fontSize: 11 },
      };
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: t.font, color: t.text },
    tooltip: {
      ...tooltipBase(t),
      trigger: "item",
      formatter: (p: unknown) => {
        const d = (p as { data: [number, number, number] }).data;
        return `<div style="color:${t.muted}">${esc(columns[d[0]] ?? "")}</div><div><b>${esc(rows[d[1]]?.name ?? "")}</b> · ${esc(rank && d[2] > 100 ? "100위 밖" : fmt.value(d[2]))}</div>`;
      },
    },
    grid: { left: 4, right: 12, top: 8, bottom: 64 },
    xAxis: { type: "category", data: columns.map(short), ...axisStyle(t), splitArea: { show: false }, splitLine: { show: false } },
    yAxis: { type: "category", data: rows.map((r) => r.name), inverse: true, ...axisStyle(t), splitLine: { show: false }, axisLabel: { ...axisStyle(t).axisLabel, width: 140, overflow: "truncate" } },
    visualMap,
    series: [
      {
        type: "heatmap",
        data,
        itemStyle: { borderColor: t.surface, borderWidth: 2, borderRadius: 3 },
        label: { show: cells <= 160, color: t.text, fontSize: 11, formatter: (p: unknown) => {
          const v = (p as { data: [number, number, number] }).data[2];
          return rank && v > 100 ? "밖" : String(v);
        } },
        emphasis: { itemStyle: { borderColor: t.text, borderWidth: 1 } },
      },
    ],
  };
}

/** 몫 (우리 n / 전체 m) — 도넛 + 가운데 비율 */
export function donutOption(spec: { value: number; total: number; label?: string; rest?: string }, t: ChartTheme): EChartsOption {
  const value = Math.max(0, spec.value);
  const rest = Math.max(0, spec.total - value);
  const pct = spec.total ? Math.round((value / spec.total) * 1000) / 10 : 0;
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: t.font, color: t.text },
    title: {
      text: `${pct}%`,
      subtext: `${value} / ${spec.total}`,
      left: "center",
      top: "center",
      itemGap: 4,
      textStyle: { color: t.text, fontSize: 22, fontWeight: 650, fontFamily: t.font },
      subtextStyle: { color: t.muted, fontSize: 12, fontFamily: t.font },
    },
    tooltip: { ...tooltipBase(t), trigger: "item", formatter: (p: unknown) => {
      const x = p as { name: string; value: number };
      return `${esc(x.name)} · <b>${x.value}</b>`;
    } },
    series: [
      {
        type: "pie",
        radius: ["66%", "88%"],
        silent: false,
        label: { show: false },
        labelLine: { show: false },
        startAngle: 90,
        data: [
          { name: spec.label ?? "우리 항목", value, itemStyle: { color: t.accent } },
          { name: spec.rest ?? "다른 항목", value: rest, itemStyle: { color: t.surfaceMuted } },
        ],
      },
    ],
  };
}

/** 표·카드 안 작은 추이선 (축·툴팁 없음, 마지막 점 강조) */
export function sparklineOption(values: (number | null)[], t: ChartTheme, rank = false): EChartsOption {
  const valid = values.filter((v) => v != null).length;
  let last = -1;
  values.forEach((v, i) => v != null && (last = i));
  return {
    backgroundColor: "transparent",
    animation: false,
    grid: { left: 3, right: 4, top: 4, bottom: 4 },
    xAxis: { type: "category", show: false, boundaryGap: valid < 2, data: values.map((_, i) => i) },
    yAxis: { type: "value", show: false, inverse: rank, scale: true },
    series: [
      {
        type: "line",
        data: values,
        connectNulls: false,
        silent: true,
        symbol: "circle",
        showSymbol: true,
        symbolSize: (_: unknown, p: { dataIndex: number }) => (valid < 3 || p.dataIndex === last ? 5 : 0),
        lineStyle: { width: 1.8, color: t.accent },
        itemStyle: { color: t.accent },
        areaStyle: rank ? undefined : { color: alpha(t.accent, 0.12) },
      },
    ],
  };
}

export type TimelineRow = { label: string; segments: { from: string; to: string; ok: boolean }[] };

/** 기기별 연결 구간 (시간 축 막대). 받지 못한 구간은 비워 둔다 */
export function timelineOption(spec: { from: string; to: string; rows: TimelineRow[]; okLabel?: string; badLabel?: string }, t: ChartTheme): EChartsOption {
  const okLabel = spec.okLabel ?? "연결";
  const badLabel = spec.badLabel ?? "끊김";
  const kst = (ms: number) => new Date(ms).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const data = spec.rows.flatMap((r, y) =>
    r.segments.map((s) => ({ value: [y, Date.parse(s.from), Date.parse(s.to), s.ok ? 1 : 0], itemStyle: { color: s.ok ? t.ok : t.fail } })),
  );
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: t.font, color: t.text },
    legend: {
      show: true,
      top: 0,
      right: 0,
      selectedMode: false,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: t.muted, fontSize: 11 },
      data: [
        { name: okLabel, itemStyle: { color: t.ok } },
        { name: badLabel, itemStyle: { color: t.fail } },
      ],
    },
    tooltip: {
      ...tooltipBase(t),
      trigger: "item",
      formatter: (p: unknown) => {
        const v = (p as { value: [number, number, number, number] }).value;
        return `<b>${esc(spec.rows[v[0]]?.label ?? "")}</b><div>${kst(v[1])} ~ ${kst(v[2])}</div><div>${v[3] ? okLabel : badLabel}</div>`;
      },
    },
    grid: { left: 4, right: 12, top: 28, bottom: 4 },
    xAxis: {
      type: "time",
      min: Date.parse(spec.from),
      max: Date.parse(spec.to),
      ...axisStyle(t),
      axisLabel: { ...axisStyle(t).axisLabel, formatter: (v: number) => new Date(v).toLocaleTimeString("ko-KR", { timeZone: "Asia/Seoul", hour: "2-digit", minute: "2-digit", hour12: false }) },
    },
    yAxis: { type: "category", data: spec.rows.map((r) => r.label), inverse: true, ...axisStyle(t), splitLine: { show: false }, axisLabel: { ...axisStyle(t).axisLabel, width: 120, overflow: "truncate" } },
    series: [
      // 범례용 빈 계열 (색 표시만)
      { name: okLabel, type: "bar", data: [], itemStyle: { color: t.ok } },
      { name: badLabel, type: "bar", data: [], itemStyle: { color: t.fail } },
      {
        type: "custom",
        encode: { x: [1, 2], y: 0 },
        data,
        renderItem: (params: unknown, api: unknown) => {
          const a = api as { value: (i: number) => number; coord: (v: [number, number]) => [number, number]; size: (v: [number, number]) => [number, number]; visual: (k: string) => string };
          const ps = params as { coordSys: { x: number; y: number; width: number; height: number } };
          const y = a.value(0);
          const start = a.coord([a.value(1), y]);
          const end = a.coord([a.value(2), y]);
          const h = Math.min(18, a.size([0, 1])[1] * 0.55);
          // 그림 영역 밖은 잘라 낸다 (축 범위 밖 구간)
          const c = ps.coordSys;
          const x1 = Math.max(start[0], c.x);
          const x2 = Math.min(end[0], c.x + c.width);
          if (x2 <= x1) return undefined;
          return { type: "rect", shape: { x: x1, y: start[1] - h / 2, width: Math.max(1, x2 - x1), height: h, r: 3 }, style: { fill: a.visual("color") } };
        },
      },
    ] as EChartsOption["series"],
  };
}
